/**
 * A list of horizontal bars — one row per bucket, each a link where it leads.
 *
 * The small, honest chart for a breakdown: label, bar, count. Used by the
 * custom report's breakdowns and by a snapshot page, which draws the numbers
 * a report saved without re-running it. A row with an `href` is a drill
 * (roadmap RP-7): the item list opens filtered to that bucket.
 */

import { Link } from 'react-router-dom';
import { resolveToken } from '../../../../components/ds/chartTheme';

export interface BarListRow {
  label: string;
  value: number;
  href?: string;
  /** Concrete colour; defaults to slate. */
  color?: string;
}

export interface ReportBarListProps {
  rows: ReadonlyArray<BarListRow>;
  /** Bars are scaled to this; defaults to the largest row. */
  max?: number;
  emptyLabel?: string;
  className?: string;
}

export function ReportBarList({ rows, max, emptyLabel = 'Nothing to show', className = '' }: ReportBarListProps) {
  if (rows.length === 0) {
    return <p className={`text-sm italic text-[var(--text-ter)] ${className}`}>{emptyLabel}</p>;
  }
  const scale = Math.max(1, max ?? Math.max(...rows.map((r) => r.value)));
  const slate = resolveToken('--slate');
  return (
    <ul className={`space-y-2.5 ${className}`}>
      {rows.map((row) => {
        const bar = (
          <>
            <span className="w-32 flex-shrink-0 truncate text-sm text-[var(--text-sec)]" title={row.label}>{row.label}</span>
            <span className="h-2 flex-1 overflow-hidden bg-[var(--hover-tint)]">
              <span className="block h-full" style={{ width: `${Math.round((row.value / scale) * 100)}%`, backgroundColor: row.color ?? slate }} />
            </span>
            <span className="w-8 flex-shrink-0 text-right font-mono text-xs tabular-nums text-[var(--text-ter)]">{row.value}</span>
          </>
        );
        return (
          <li key={`${row.label}:${row.href ?? ''}`}>
            {row.href ? (
              <Link to={row.href} className="group flex items-center gap-3 hover:[&>span:first-child]:text-[var(--text-pri)]" title={`Open the items: ${row.label}`}>
                {bar}
              </Link>
            ) : (
              <span className="flex items-center gap-3">{bar}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
